import React,{cloneElement,useEffect,useId,useRef,useState} from 'react';
import {createPortal} from 'react-dom';
import {popoverPosition} from './popover.js';

export default function HoverPopover({children,content,label,interactive=false,onlyWhenTruncated=false}){
  const id=useId(),trigger=useRef(),panel=useRef(),timer=useRef(),pinned=useRef(false),restoringFocus=useRef(false);
  const [open,setOpen]=useState(false),[position,setPosition]=useState(null);
  function cancelClose(){clearTimeout(timer.current);}
  function close(){
    cancelClose();pinned.current=false;setOpen(false);setPosition(null);
    if(panel.current?.contains(document.activeElement)){
      restoringFocus.current=true;trigger.current?.focus();restoringFocus.current=false;
    }
  }
  function show(){
    if(restoringFocus.current)return;
    cancelClose();const element=trigger.current;
    if(onlyWhenTruncated&&(!element||element.scrollWidth<=element.clientWidth+1))return;
    setOpen(true);
  }
  function leave(){
    cancelClose();timer.current=setTimeout(()=>{
      if(pinned.current||trigger.current?.contains(document.activeElement)||panel.current?.contains(document.activeElement))return;
      close();
    },160);
  }
  useEffect(()=>()=>clearTimeout(timer.current),[]);
  useEffect(()=>{
    if(!open)return;
    function place(){
      if(!trigger.current||!panel.current)return;
      const anchor=trigger.current.getBoundingClientRect();
      if(anchor.bottom<0||anchor.top>window.innerHeight){close();return;}
      const size=panel.current.getBoundingClientRect();
      // scrollHeight retains the natural content height when the panel is constrained.
      setPosition(popoverPosition(anchor,{width:size.width,height:Math.max(size.height,panel.current.scrollHeight+2)},{width:window.innerWidth,height:window.innerHeight}));
    }
    function outside(event){if(!trigger.current?.contains(event.target)&&!panel.current?.contains(event.target))close();}
    function escape(event){
      if(event.key==='Escape'){
        close();
        event.preventDefault();event.stopPropagation();
      }
    }
    place();const observer=typeof ResizeObserver!=='undefined'?new ResizeObserver(place):null;
    observer?.observe(trigger.current);observer?.observe(panel.current);
    window.addEventListener('resize',place);window.addEventListener('scroll',place,true);
    document.addEventListener('pointerdown',outside);document.addEventListener('keydown',escape,true);
    return()=>{observer?.disconnect();window.removeEventListener('resize',place);window.removeEventListener('scroll',place,true);document.removeEventListener('pointerdown',outside);document.removeEventListener('keydown',escape,true);};
  },[open]);
  const triggerProps={ref:trigger,onPointerEnter:event=>{children.props.onPointerEnter?.(event);if(event.pointerType!=='touch')show();},onPointerLeave:event=>{children.props.onPointerLeave?.(event);leave();},onFocus:event=>{children.props.onFocus?.(event);show();},onBlur:event=>{children.props.onBlur?.(event);leave();}};
  if(interactive)Object.assign(triggerProps,{'aria-haspopup':'dialog','aria-expanded':open,'aria-controls':open?id:undefined,onClick:event=>{children.props.onClick?.(event);if(event.defaultPrevented)return;cancelClose();if(pinned.current)close();else{pinned.current=true;setOpen(true);}},onKeyDown:event=>{
    children.props.onKeyDown?.(event);
    if(event.key==='ArrowDown'||(event.key==='Tab'&&!event.shiftKey&&open)){
      event.preventDefault();show();requestAnimationFrame(()=>panel.current?.querySelector('button,a')?.focus());
    }
  }});
  else triggerProps['aria-describedby']=open?id:undefined;
  return <>{cloneElement(children,triggerProps)}{open&&typeof document!=='undefined'&&createPortal(<div ref={panel} id={id} role={interactive?'dialog':'tooltip'} aria-label={interactive?label:undefined} tabIndex={interactive?-1:undefined} className={`hover-popover ${interactive?'w-[360px]':'w-max max-w-[360px] px-3 py-2.5 text-xs leading-5 text-cloud'}`} style={position||{visibility:'hidden'}} onClick={event=>event.stopPropagation()} onPointerEnter={cancelClose} onPointerLeave={leave} onFocus={cancelClose} onBlur={leave}>{typeof content==='function'?content(close):content}</div>,document.body)}</>;
}
